<template>
  <div id="Model">
    <div id="transparentContainer" class="frosted-glass" v-if="initialLoad === true" ></div>

    <div id="legendContainer" class="">
      <div id="legendBox">
        <div id="legendPanel">
          <div class="legend-parent d-flex align-items-center" v-for="(l,i) in legendData" :key="i">
            <div class="legend-color mr-1" :style="{'background-color': l.color}"></div>
            <div class="legend-label custom-label">{{l.label}}</div>  
          </div>
        </div>
        <div id="tooltipPanel" class="p-1">
          <div class="legend-label custom-label text-center">
            <span v-if="mobileFlag === true">Touch</span> 
            <span v-if="mobileFlag === false">Click</span> to rotate<span v-if="mobileFlag === false">, scroll to zoom, and right click to move</span>
          </div>
        </div>
      </div>
    </div>

    <div id="canvasContainer" ref="canvasContainer" class="canvas-container">
      <canvas id="canvas" ref="canvas"></canvas>
    </div>

  </div>
</template>

<script>
/**
 * import THREEjs library
 */
const THREE = require('three');
const loader = new THREE.ObjectLoader();
/**
 * import orbit controls library
 */
const OrbitControls = require('three-orbit-controls')(THREE)

import { csv } from 'd3-fetch';

let scene = [], controls = null, camera = null, renderer = null;
let modelSettings = {}
let metricStatus = false;
let selectedMetric = null;
const entourgaeScale = 1.4;

const treeModels = {};

import { mapGetters } from 'vuex';

import {
  ConstructScene,
  Lights,
  colorMesh,
  createCopies
} from '@/assets/js/helpers.js';

export default {
  name: 'Model',
  props:{
  },
  data(){
    return {
      initialLoad: true,
      legendData:[
        {'label': "unoccupied", "color": "#F4D4A6"},
        {'label': "commercial", "color": "#8FA4BF" },
        {'label': "solar panels", "color": "#969696"},
        {'label': "public space", "color": "#038C25"}
      ],
      mobileFlag: false
    }
  },
  components: {
  },
  created(){
    this.subscribeToStore();
    this.$store.dispatch('readModelData');
  },
  mounted(){
    this.init();
  },
  computed:{
    ...mapGetters({
      modelData: 'getModelData',
      modelID: "getModelID",
      previousModelID: "getPreviousModelID",
      metricID: "getMetricID",
      previousMetricID: "getPreviousMetricID",
      resetMetrics: "getResetMetrics"
    }),
    canvasContainer(){
      return this.$refs['canvasContainer']
    },
    canvas(){
      return this.$refs['canvas']
    },
    modelIDSplit(){
      return this.modelID.split("_option.json")[0];
    }
  },
  methods:{
    resetMetric () {
      if(metricStatus === true){
        this.$refs['MetricContainer'].clearMetricPanel(null);
        this.removeModel(this.modelIDSplit+"_"+ this.metricID +".json");
        this.$store.commit('setPreviousMetricID', "");
      }
    },
    init () {
      // SCENE
      const createScene = new ConstructScene(
        THREE.Scene,
        new THREE.Color('rgb(28, 83, 89)'),
        new THREE.Fog(0x1a2050, 10000, 10000)
      )

      scene = createScene.scene

      // CAMERA
      createScene.constructCamera(THREE.PerspectiveCamera, 0)

      camera = createScene.camera
      // camera.zoom = 1.5

      renderer = new THREE.WebGLRenderer({
        "canvas": this.canvas,
        antialias: true
      });

      const sceneBlue = new THREE.Color("#1C5359")
      renderer.setClearColor(sceneBlue, 1);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(this.canvasContainer.clientWidth, this.canvasContainer.offsetHeight);

      // CONTROLS
      createScene.constructControls(OrbitControls, renderer.domElement)
      controls = createScene.controls
      
      // SETUP LIGHTS
      const lights = new Lights(
        THREE.AmbientLight,
        THREE.DirectionalLight,
        THREE.CameraHelper
      )

      lights.createDirectionalLight()

      scene.add(lights.ambient)
      scene.add(lights.directionalLight)

      this.canvasContainer.appendChild(renderer.domElement)

      window.addEventListener('resize', this.render, false);

      this.onWindowResize();
      this.render();

    },
    removeModel(modelName){
      let modelToRemove = scene.getObjectByName(modelName)

      modelToRemove.children.forEach(function (child) {
        child.geometry.dispose()
        child.material.dispose()
        child = undefined
      });

      scene.remove(modelToRemove)

      modelToRemove = undefined

      renderer.dispose()
      renderer.renderLists.dispose();

      controls.update();
    },
    addModel(model){
      model.position.set(
        modelSettings.x, 
        modelSettings.y, 
        modelSettings.z
      );

      scene.add(model);
      this.render();

      this.setLoadingStatus(false);
    },
    controlsUpdate(){
      controls.enablePan = !this.mobileFlag;
      controls.enableZoom = !this.mobileFlag;
    },
    testMobile(){
      this.mobileFlag =  window.innerWidth <= 500 ? true : false
    },
    onWindowResize () {
      this.testMobile();
      this.controlsUpdate();
      camera.aspect = (this.canvasContainer.clientWidth / this.canvasContainer.offsetHeight)
      camera.updateProjectionMatrix()
      
      renderer.setSize(this.canvasContainer.clientWidth, this.canvasContainer.offsetHeight)
    },
    getModelFromStore (modelName, type) {
      return new Promise((resolve) =>{
        this.$store.dispatch('getModelByID', [modelName, type]).then((res)=>{
          resolve(res);
        });
      })
    },
    setupTrees(treeData){
      const sceneName = 'trees'

      if(scene.getObjectByName(sceneName)){

        let modelToRemove = scene.getObjectByName(sceneName)

        modelToRemove.children.forEach((child)=>{
          child.children.forEach((obj)=>{
            obj.geometry.dispose()
            obj.material.dispose()
            obj = undefined
          });

          child = undefined;
        });

        scene.remove(modelToRemove);

        modelToRemove = undefined;

        renderer.dispose();
        renderer.renderLists.dispose();
      }

      const localScene = createCopies(treeData, sceneName, 1, treeModels);

      localScene.position.set(
        modelSettings.x, 
        modelSettings.y, 
        modelSettings.z
      )
      scene.add(localScene);

      controls.update();
    },
    placeEntourage (){
      csv("./data/trees.csv").then((data)=>{
        const modelNames = [...new Set(data.map(item => item.type))];

        let modelLoadPromise = new Promise((resolve, reject) => {
          let loaded = 0;
          modelNames.forEach((modelName) => {
            modelName = modelName + ".json"
            const filePath = ["./models",modelName].join("/");

            loader.load(filePath, (model)=>{

              treeModels[modelName] = model
              loaded += 1

              if( loaded === modelNames.length){
                resolve("create copies")
              }
            }, function(){
              // console.debug(xhr);
            }, function(err){
              if(err) reject();
            });
          });
        });

        modelLoadPromise.then(()=>{});
      })
      csv("./data/ruths.csv").then((data)=>{

        const modelCopyData = data;
        let loadedModels = {}
        const modelNames = [...new Set(modelCopyData.map(item => item.type))];

        let modelLoadPromise = new Promise((resolve, reject) => {
          let loaded = 0;
          modelNames.forEach((modelName) => {
            modelName = modelName + ".json"
            const filePath = ["./models",modelName].join("/");

            loader.load(filePath, (model)=>{

              loadedModels[modelName] = model
              loaded += 1

              if( loaded === modelNames.length){
                resolve("create copies")
              }
            }, function(){
              // console.debug(xhr);
            }, function(err){
              if(err) reject();
            });
          });
        });

        modelLoadPromise.then(() => {
          
          const localScene = createCopies(data, 'ruths', entourgaeScale, loadedModels);

          localScene.position.set(
            modelSettings.x, 
            modelSettings.y, 
            modelSettings.z
          )
          scene.add(localScene);

          controls.update();
          loadedModels = {};
        });
      });
    },
    setupScene (defaultModel, contextModel) {

      const bb = new THREE.Box3()

      bb.expandByObject(defaultModel);
      bb.expandByObject(contextModel);

      let center = new THREE.Vector3()

      bb.getCenter(center)

      modelSettings = { 
        x: -center.x,
        y: center.y, 
        z: -center.z 
      }

      defaultModel.position.set(
        modelSettings.x, 
        modelSettings.y, 
        modelSettings.z
      )
      contextModel.position.set(
        modelSettings.x, 
        modelSettings.y, 
        modelSettings.z
      );

      const ms = bb.getBoundingSphere(new THREE.Sphere())

      let cameraRadius = ms.radius / 2 * (1 + Math.sqrt(5))

      camera.position.set(cameraRadius, cameraRadius, -cameraRadius);
      camera.updateProjectionMatrix()

      controls.addEventListener("change", this.render)
      controls.target.set(0, modelSettings.y, 0);
      // controls.enablePan = false;

      scene.add(defaultModel);
      scene.add(contextModel);

      this.placeEntourage();

      controls.update();

      this.initialLoad = false

      this.setLoadingStatus(false);
    },
    render () {
      this.onWindowResize();
      // uncomment to create animaton loop
      // requestAnimationFrame(this.render);
      // controls.update()

      renderer.render(scene, camera);
    },
    metricStatus(metric){
      metricStatus = metric.model;
      selectedMetric = metric;
    },
    setLoadingStatus(toggle){
      this.$store.commit('setLoadingFlag', toggle);
    },
    subscribeToStore(){
      this.$store.subscribe((mutation) =>{
        switch (mutation.type) {
          case "setMetricID":

          if(this.previousMetricID.length > 0){
            const prevMetric = this.modelIDSplit+"_"+ this.previousMetricID +".json";
            this.removeModel(prevMetric);
          }

          if( metricStatus === true ){
            this.setLoadingStatus(true);
            
            const metricID = this.modelIDSplit+"_"+ this.metricID +".json";

            this.getModelFromStore(metricID, 'metric').then((modelData)=>{
              const [ model ] = modelData;

              const coloredModel = colorMesh(model, selectedMetric.colorScale);

              this.addModel(coloredModel);
            });
          }
        }
      });
      this.$store.subscribe((mutation) =>{
        switch (mutation.type) {
          case "setModelID":
          this.getModelFromStore(this.modelID, 'model').then((modelData)=>{
            const [ model, treeData ] = modelData;

            if(treeData !== null){
              this.setupTrees(treeData);
            }
            
            this.removeModel(this.previousModelID);
            this.addModel(model);
          });
        }
      })
      this.$store.subscribe((mutation) => {
        switch (mutation.type) {
          case 'setModelData':
          this.getModelFromStore('context.json', 'context').then((contextModel)=>{
            this.getModelFromStore(this.modelID, 'model').then((defaultModel)=>{
              this.setupScene(defaultModel[0], contextModel[0])
            });
          });
        }
      });
      this.$store.subscribe((mutation) =>{
        switch (mutation.type) {
          case 'resetMetrics':

            if(this.resetMetrics){
              this.resetMetric();
            }
        }
      })
    }
  },
}
</script>

<style lang="scss">

$legend-color-size: 15px;

#Model{
  height: $model-size;
  width: 100%;
  background-color: rgb(28, 83, 89);
  position: relative;

  #canvasContainer{
    width: 100%;
    height: 100%;
    background: $blue;
  }

  #canvas{
    height: 100%;
    width: 100%;
  }

  #transparentContainer{
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 5;
    background: transparent;
  }
  
  #mobileLoadingContainer{
    height: $model-size;
    width: 100%;
  }

  #legendContainer{
    position: absolute;
    top: 0;
    width: 100%;
    display: flex;
    justify-content: center;

    #legendBox{
      display: flex;
      background: $blue;
      flex-direction: column;
      width: $max-container-width;
      padding: 0.5rem 0.2rem;

      #legendPanel{
        display: flex;
        justify-content: space-around;
      }

      #tooltipPanel{

      }
    }

    .custom-label{
      font-size: $font-sm;
      font-weight: $font-med;
      color: $off-white;
    }
    .legend-parent{
      display: inline-flex;
    }
    .legend-color{
      height: $legend-color-size;
      width: $legend-color-size;

      border-radius: 4px;
    }
  }
}
</style>
